<template>
  <section
    ref="view"
    class="lab-view"
    :class="{ 'pseudo-full': pseudoFull }"
    :aria-label="t.wall"
  >
    <div class="lab-view-tools">
      <div>
        <button
          type="button"
          :aria-pressed="mode === 'flat'"
          @click="setMode('flat', 'button')"
        >
          {{ t.flat }}</button
        ><button
          type="button"
          :aria-pressed="mode === 'space'"
          :disabled="reduced"
          @click="setMode('space', 'button')"
        >
          {{ t.space }}
        </button>
      </div>
      <div v-if="mode === 'space'" class="lab-clusters">
        <button
          v-for="group in LAB_GROUPS"
          :key="group.id"
          type="button"
          :aria-pressed="theme === group.id"
          @click="pickTheme(group.id)"
        >
          <i :style="{ background: LAB_PIN_TONES[group.id] }"></i>
          {{ localText(group.title, lang) }}</button
        ><button
          type="button"
          :aria-pressed="theme === null"
          @click="pickTheme(null)"
        >
          {{ t.allThemes }}
        </button>
      </div>
      <div v-if="mode === 'space'" class="lab-navigation">
        <button
          type="button"
          :aria-label="t.left"
          @click="engine?.pan(-130, 0)"
        >
          ←</button
        ><button
          type="button"
          :aria-label="t.right"
          @click="engine?.pan(130, 0)"
        >
          →</button
        ><button type="button" :aria-label="t.up" @click="engine?.pan(0, 130)">
          ↑</button
        ><button
          type="button"
          :aria-label="t.down"
          @click="engine?.pan(0, -130)"
        >
          ↓</button
        ><button
          type="button"
          :aria-label="t.zoomIn"
          @click="engine?.zoom(1.16)"
        >
          ＋</button
        ><button
          type="button"
          :aria-label="t.zoomOut"
          @click="engine?.zoom(0.86)"
        >
          −</button
        ><button type="button" @click="engine?.reset()">{{ t.reset }}</button
        ><button
          type="button"
          class="lab-fullscreen"
          :aria-pressed="fullscreen"
          @click="toggleFullscreen"
        >
          {{ fullscreen ? t.exitFullscreen : t.fullscreen }}
        </button>
      </div>
    </div>
    <p v-if="fallback" class="lab-fallback" role="status">{{ t.fallback }}</p>
    <p v-if="reduced" class="lab-fallback">{{ t.reduced }}</p>
    <div
      v-if="mode === 'space'"
      ref="wall"
      class="lab-wall"
      data-lenis-prevent
      tabindex="0"
      :aria-label="t.controls"
      @keydown="keyboard"
      @pointerdown="dragStart"
      @pointermove="dragMove"
      @pointerup="dragEnd"
      @pointercancel="dragEnd"
    >
      <!-- Nailed to the top left of the frame, not to the board: it is the
           label on the outside of the case, so it never rides the projection
           and never lands on a sheet of paper. The two dates stack, because a
           card narrow enough to clear the first month tab is a card that
           leaves the whole rail readable. -->
      <div class="lab-case-card" aria-hidden="true">
        <i class="lab-case-pin"></i>
        <b>{{ t.caseFile }}</b>
        <span>{{ period.start }}</span>
        <span>{{ period.end }}</span>
        <span>{{ count }} {{ t.recordsWord }}</span>
      </div>
      <div class="lab-month-rail" :aria-label="t.monthRail">
        <button
          v-for="mark in visibleMonths"
          :key="mark.key"
          type="button"
          class="lab-month"
          :class="{ tight: mark.tight }"
          :style="{ left: mark.x + 'px', top: mark.y + 'px' }"
          :aria-label="t.jumpTo + ' ' + (mark.ongoing ? t.undated : mark.key)"
          @click="engine?.month(mark.key)"
        >
          <i></i>{{ mark.text }}
        </button>
      </div>
      <div
        class="lab-edge-layer"
        aria-hidden="true"
        :style="{ '--tag-size': tagSize + 'px' }"
      >
        <template v-for="edge in visibleEdges" :key="edge.key">
          <i
            v-if="edge.leader"
            class="lab-edge-leader"
            :class="{ theme: edge.theme }"
            :style="edge.leader"
          ></i>
          <span
            class="lab-edge-tag"
            :class="{ theme: edge.theme }"
            :style="{ left: edge.x + 'px', top: edge.y + 'px' }"
            >{{ localText(edge.label, lang) }}</span
          >
        </template>
      </div>
      <div class="lab-paper-layer">
        <button
          v-for="(item, index) in items"
          :key="item.id"
          :data-lab-id="item.id"
          type="button"
          class="lab-paper"
          :class="[
            'shape-' + (projection(item).shape || shapeOf(item)),
            'fasten-' + fastenOf(item),
            shapeOf(item) === 'ticket' ? 'tone-' + ticketTone(item) : '',
            {
              selected: selected === item.id,
              neighbor: neighbors.has(item.id),
              'has-image': !!image(item),
              muted: theme !== null && groupOf(item) !== theme,
              'zoom-mid': (projection(item).width || 0) >= ZOOM_MID,
              'zoom-near': (projection(item).width || 0) >= ZOOM_NEAR,
            },
          ]"
          :style="paperStyle(item, index)"
          :tabindex="projection(item).visible === false ? -1 : 0"
          :aria-pressed="selected === item.id"
          @click="emit('select', item.id)"
          @pointerenter="lift(item.id)"
          @pointerleave="lift(null)"
          @focus="lift(item.id, 'focused')"
          @blur="lift(null, 'focused')"
        >
          <!-- The sheet is the only thing that clips. A torn notebook edge is
               a clip-path and a photograph must stop at the paper's edge, so
               everything printed on the paper lives in here; everything stuck
               on top of it - tape, eyelet, pins, notes - is a sibling, and
               hangs over the edge the way it does on a real board. -->
          <span class="lab-sheet"
            ><b v-if="shapeOf(item) === 'clipping'" class="lab-masthead">{{
              masthead(item)
            }}</b
            ><b v-else-if="!image(item)" class="lab-kind-line">{{
              kindLine(item)
            }}</b
            ><img
              v-if="image(item)"
              :src="image(item)"
              alt=""
              loading="lazy"
            /><span class="lab-paper-copy"
              ><small>{{ item.date || t.undated }}</small
              ><strong>{{ localText(item.title, lang) }}</strong
              ><span v-if="!image(item)" class="lab-paper-keywords">{{
                (item.keywords || [])
                  .slice(0, 3)
                  .map((k) => localText(k, lang))
                  .join(" · ")
              }}</span
              ><span
                v-if="
                  !image(item) || ['exhibit', 'clipping'].includes(shapeOf(item))
                "
                class="lab-paper-lede"
                >{{ lede(item) }}</span
              ></span
            ><i
              v-if="shapeOf(item) === 'capture'"
              class="lab-credit"
              aria-hidden="true"
              >Che-Yu Wu / capture</i
            ><i
              v-if="shapeOf(item) === 'ticket'"
              class="lab-stub"
              aria-hidden="true"
              >{{ stub(item) }}</i
            ></span
          >
          <template v-if="fastenOf(item) === 'tape'"
            ><i class="lab-tape left" aria-hidden="true"></i
            ><i class="lab-tape right" aria-hidden="true"></i
          ></template>
          <i
            v-if="shapeOf(item) === 'tag'"
            class="lab-tag-eyelet"
            aria-hidden="true"
          ></i>
          <i
            v-for="slot in pinsOf(item)"
            :key="'pin' + slot"
            class="lab-pin"
            :class="['slot-' + slot, 'pin-' + pinOf(item)]"
            aria-hidden="true"
          ></i>
          <i
            v-for="note in stickies(item)"
            :key="note.slot"
            class="lab-sticky"
            :class="note.slot + ' tone-' + note.tone"
            aria-hidden="true"
            >{{ note.text }}</i
          >
        </button>
      </div>
      <svg class="lab-string-layer" aria-hidden="true">
        <polyline
          v-for="edge in stringEdges"
          :key="edge.key"
          :points="edge.line"
          :class="{ theme: edge.theme, dim: edge.dim }"
          :stroke-width="edge.theme ? twineWidth : stringWidth"
        />
      </svg>
      <svg class="lab-string-layer front" aria-hidden="true">
        <polyline
          v-for="edge in focusEdges"
          :key="edge.key"
          :points="edge.line"
          :class="{ theme: edge.theme }"
          :stroke-width="edge.theme ? twineWidth : stringWidth"
        />
      </svg>
      <span class="lab-wall-hint">{{ t.hint }}</span>
    </div>
    <!-- Flat reading is no longer a pile of cards in three columns. It is the
         year read downwards: one section per month, a length of twine down
         the left of each with the month tab pinned to it, and the records of
         that month in two columns beside it. On a phone this is the whole
         experience, so every relation the wall would draw as a string is a
         small tag under the card that jumps to the other record. -->
    <div v-else class="lab-flat-preview">
      <div class="lab-flat-top">
        <div class="lab-case-card flat" aria-hidden="true">
          <i class="lab-case-pin"></i>
          <b>{{ t.caseFile }}</b>
          <span>{{ period.start }} / {{ period.end }}</span>
          <span>{{ count }} {{ t.recordsWord }}</span>
        </div>
        <button
          v-if="!reduced"
          type="button"
          class="lab-flat-enter"
          @click="enterWall"
        >
          {{ t.onTheWall }} ↗︎
        </button>
        <p class="lab-flat-hint">{{ t.flatHint }}</p>
      </div>
      <section
        v-for="segment in flatMonths"
        :key="segment.key"
        class="lab-flat-month"
      >
        <h3 class="lab-flat-month-tab">{{ segment.text }}</h3>
        <div class="lab-flat-cards">
          <article
            v-for="item in segment.items"
            :id="'lab-flat-' + item.id"
            :key="item.id"
            class="lab-flat-card"
            :class="[
              'shape-' + shapeOf(item),
              { selected: selected === item.id, flash: flashed === item.id },
            ]"
          >
            <i class="lab-flat-pin" :class="'pin-' + pinOf(item)"></i>
            <button
              :data-lab-id="item.id"
              type="button"
              class="lab-flat-open"
              @click="emit('select', item.id)"
            >
              <img
                v-if="image(item)"
                :src="image(item)"
                alt=""
                loading="lazy"
              /><span
                ><small>{{ item.date || t.undated }}</small
                ><strong>{{ localText(item.title, lang) }}</strong
                ><span>{{
                  (item.keywords || [])
                    .slice(0, 3)
                    .map((k) => localText(k, lang))
                    .join(" · ")
                }}</span></span
              >
            </button>
            <ul v-if="tiesOf(item).length" class="lab-flat-ties">
              <li v-for="tie in tiesOf(item)" :key="tie.id">
                <button
                  type="button"
                  :class="{ theme: tie.type === 'theme' }"
                  @click="jumpTo(tie.id)"
                >
                  {{ localText(tie.label, lang) }}
                </button>
              </li>
            </ul>
            <i
              v-for="note in stickies(item)"
              :key="note.slot"
              class="lab-sticky"
              :class="note.slot + ' tone-' + note.tone"
              aria-hidden="true"
              >{{ note.text }}</i
            >
          </article>
        </div>
      </section>
    </div>
  </section>
</template>
<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import {
  localText,
  labRelations,
  labLayout,
  labPaperShape,
  labPinTone,
  labImage,
  labStickyCount,
  labFasten,
  labPinSlots,
  labGroup,
  labOrdered,
  labHash,
  LAB_GROUPS,
  LAB_PIN_TONES,
  LAB_KINDS,
} from "@/runtime/lab-model.mjs";
import { trackEvent } from "@/runtime/analytics.mjs";
const props = defineProps({
    items: { type: Array, required: true },
    lang: { type: String, default: "en" },
    selected: { type: String, default: null },
    period: { type: Object, default: () => ({ start: "", end: "" }) },
    count: { type: Number, default: 0 },
    // Which year of the wall this is. It rides on the three events so the
    // reports can say which board people actually explore, not just that
    // somebody explored one.
    year: { type: String, default: "" },
  }),
  emit = defineEmits(["select"]);
const mode = ref("flat"),
  view = ref(null),
  fullscreen = ref(false),
  pseudoFull = ref(false),
  reduced = ref(false),
  fallback = ref(false),
  wall = ref(null),
  positions = ref([]),
  projectedEdges = ref([]),
  allEdges = ref([]),
  projectedMonths = ref([]),
  viewBox = ref({ width: 0, height: 0 }),
  wallScale = ref(1),
  wallZoom = ref(1),
  pointed = ref(null),
  focused = ref(null),
  // Which curated theme the reader asked the board to pick out, and which
  // card the flat timeline just jumped to.
  theme = ref(null),
  flashed = ref(null);
let flashTimer = 0;
let engine,
  media,
  epoch = 0,
  drag = null;
// The spatial wall costs a three.js bundle and a WebGL context. Record every
// transition with the reason, so the question "is anyone using it, and does it
// survive their device" has an answer instead of an opinion.
function setMode(next, trigger) {
  if (mode.value === next) return;
  mode.value = next;
  trackEvent("lab_view_mode", {
    mode: next,
    trigger,
    lang: props.lang,
    year: props.year,
  });
}
const messages = {
  zh: {
    curated: "策展主題",
    wall: "創作關係牆",
    flat: "平面閱讀",
    space: "空間探索",
    left: "向左移動",
    right: "向右移動",
    up: "向上移動",
    down: "向下移動",
    zoomIn: "放大",
    zoomOut: "縮小",
    reset: "重設視角",
    fullscreen: "全螢幕",
    exitFullscreen: "離開全螢幕",
    caseFile: "案件紀錄",
    recordsWord: "筆",
    fallback: "空間視圖目前無法使用，所有內容仍可在平面閱讀。",
    reduced: "已依減少動態偏好使用平面閱讀。",
    controls: "創作牆，方向鍵平移，加減鍵縮放，Home重設",
    monthRail: "月份索引",
    jumpTo: "跳到",
    hint: "拖曳空白處移動，滾輪縮放，方向鍵與按鈕也可操作",
    flatHint:
      "這裡依月份往下讀，每張卡下面的小標是它的關係，點一下會跳到另一筆紀錄",
    undated: "持續進行",
    allThemes: "全部",
    onTheWall: "在牆上看",
    ongoingMonth: "持續進行",
    yearOnly: "全年",
  },
  en: {
    curated: "Curatorial themes",
    wall: "Creative connections",
    flat: "Read in a plane",
    space: "Explore in space",
    left: "Pan left",
    right: "Pan right",
    up: "Pan up",
    down: "Pan down",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    reset: "Reset view",
    fullscreen: "Fullscreen",
    exitFullscreen: "Exit fullscreen",
    caseFile: "Case file",
    recordsWord: "records",
    fallback:
      "The spatial view is unavailable. All records remain available in the flat view.",
    reduced: "Flat reading follows your reduced-motion preference.",
    controls:
      "Creative wall. Arrow keys pan, plus and minus zoom, Home resets.",
    monthRail: "Month index",
    jumpTo: "Jump to",
    hint: "Drag to move · Scroll to zoom · Arrow keys and buttons also work",
    flatHint:
      "Read the year downwards, one month at a time. The small tags under a card are its connections: open one to jump to the record it names.",
    undated: "Undated",
    allThemes: "All",
    onTheWall: "See it on the wall",
    ongoingMonth: "Still going on",
    yearOnly: "all year",
  },
};
const t = computed(() => messages[props.lang]),
  neighbors = computed(
    () => new Set(labRelations(props.items, props.selected).map((e) => e.id)),
  );
// The strings are drawn over the paper, not behind it: they are tied to pin
// heads that stand proud of the sheets. Width is measured in screen pixels and
// grows with the square root of the zoom, never linearly: string tied across a
// board stays string when you lean in, it does not become a painted bar.
const stringEdges = computed(() => allEdges.value.filter((e) => e.line));
// The strings of the sheet in the reader's hand are drawn a second time, in
// a layer above the paper. Everything on this board is small type, and a red
// net drawn over fifty sheets of it was unreadable: the net lives behind the
// paper now and one record's worth of it comes forward at a time.
const focusEdges = computed(() =>
  allEdges.value.filter((e) => e.line && e.focus),
);
const stringWidth = computed(() =>
  Math.min(3.2, Math.max(1.2, 1.6 * Math.sqrt(wallZoom.value))).toFixed(2),
);
const twineWidth = computed(() => (stringWidth.value * 0.7).toFixed(2));
// The paper in the reader's hand: under the cursor, or the one they tabbed to.
const hovered = computed(() => pointed.value || focused.value);
const tagPx = computed(() => Math.max(8, Math.min(18, 34 * wallScale.value)));
const tagSize = computed(() => tagPx.value.toFixed(1));
// A string tag that lands on a photograph reads as a caption for the wrong
// record, so the tag's own box is measured against every visible paper and
// the loser is simply not drawn.
const tagBox = (text) => {
  let units = 0;
  for (const ch of text) units += ch.charCodeAt(0) > 0x2e80 ? 1 : 0.52;
  return [
    Math.min(124, units * tagPx.value + 13),
    tagPx.value * 1.05 + 9,
  ];
};
// The rail hangs off the top of the board, but once the camera is inside a
// month the board's top edge is above the frame. The tabs then rest against
// the top of the view so the next month is always one click away.
//
// Months are no longer evenly spaced, so a quiet month is a narrow stretch and
// its tab has to earn its room. Two steps back, never a step off the rail: the
// long form with the year first, the bare two digits if that will not fit, and
// a smaller tab printed tighter if even that is wider than the gap. Every
// month in the period keeps a tab, because a month with no tab is a month a
// reader cannot click.
const MONTH_TAB_PAD = 20,
  MONTH_TIGHT_PAD = 6;
// A CJK glyph is about twice as wide as a digit; counting it as one digit
// let 「2025 · 全年」 sit on top of the tab beside it (seen on the 2025, 2016
// and 2013 walls, 2026-09-18).
const monthTabWidth = (text, tight) => {
  let units = 0;
  for (const ch of text) units += ch.charCodeAt(0) > 0x2e80 ? 1.9 : 1;
  return units * (tight ? 6 : 7) + (tight ? MONTH_TIGHT_PAD : MONTH_TAB_PAD);
};
const visibleMonths = computed(() => {
  const marks = projectedMonths.value.filter((mark) => mark.visible),
    limit = viewBox.value.height - 24,
    shown = [];
  // Two cursors, because the last step back is a second row hung just under
  // the first: a month squeezed between two neighbours drops down rather than
  // disappearing behind them.
  const reached = [-Infinity, -Infinity];
  for (const mark of marks) {
    const month = mark.ongoing
        ? t.value.ongoingMonth
        : mark.yearOnly
          ? t.value.yearOnly
          : String(mark.month).padStart(2, "0"),
      long = mark.ongoing ? month : `${mark.year} · ${month}`,
      tiers = [
        ...(mark.first ? [{ text: long, tight: false, row: 0 }] : []),
        { text: month, tight: false, row: 0 },
        { text: month, tight: true, row: 1 },
      ];
    const tab =
      tiers.find(
        (candidate) =>
          mark.x - monthTabWidth(candidate.text, candidate.tight) / 2 >
          reached[candidate.row] + 4,
      ) || tiers[tiers.length - 1];
    reached[tab.row] = Math.max(
      reached[tab.row],
      mark.x + monthTabWidth(tab.text, tab.tight) / 2,
    );
    shown.push({
      ...mark,
      y: Math.max(24, Math.min(limit || mark.y, mark.y)) + tab.row * 21,
      text: tab.text,
      tight: tab.tight,
    });
  }
  return shown;
});
// A tag half off the board is a tag nobody can read, so the seat has to hold
// the whole label inside the frame.
const inFrame = (spot, w, h) => {
  const { width, height } = viewBox.value;
  return (
    !width ||
    (spot.x - w / 2 > 6 &&
      spot.x + w / 2 < width - 6 &&
      spot.y - h / 2 > 6 &&
      spot.y + h / 2 < height - 6)
  );
};
const freeOf = (spot, w, h, taken) =>
  !taken.some(
    (q) =>
      Math.abs(q.x - spot.x) < (q.w + w) / 2 + 8 &&
      Math.abs(q.y - spot.y) < (q.h + h) / 2 + 8,
  );
const clearOf = (spot, w, h, taken) =>
  inFrame(spot, w, h) &&
  !positions.value.some(
    (p) =>
      p.visible &&
      Math.abs(p.x - spot.x) < (p.width + w) / 2 + 4 &&
      Math.abs(p.y - spot.y) < (p.height + h) / 2 + 4,
  ) &&
  freeOf(spot, w, h, taken);
const RING = [
  [0, -1],
  [0.7, -0.7],
  [1, 0],
  [0.7, 0.7],
  [0, 1],
  [-0.7, 0.7],
  [-1, 0],
  [-0.7, -0.7],
];
// Where a tag may hang, best first. On its own string is best: the label sits
// on the relation it names. Sliding along the string comes next, then a step
// off to the side of it, and last of all a berth beside one of the two nails
// with a hairline drawn back to the string.
function tagSeats(edge, w, h) {
  const seats = (edge.spots || []).map((spot) => ({ ...spot, tied: true }));
  const spots = edge.spots || [];
  for (const radius of [w / 2 + 14, h + 34, h + 64])
    for (const spot of spots.slice(0, 5))
      for (const [ux, uy] of RING)
        seats.push({ x: spot.x + ux * radius, y: spot.y + uy * radius });
  for (const end of edge.ends || [])
    for (const radius of [h + 26, h + 58, h + 96])
      for (const [ux, uy] of RING)
        seats.push({ x: end.x + ux * radius, y: end.y + uy * radius });
  // Last resort: the nearest clear patch of cork anywhere within reach of the
  // string. Fourteen relations and a dense board means a label that cannot
  // hang on its own string still has to be somewhere a reader can find it.
  const { width, height } = viewBox.value;
  if (width && edge.spots?.length) {
    const home = edge.spots[0],
      patches = [];
    for (let x = w / 2 + 10; x < width - w / 2 - 10; x += 22)
      for (let y = h / 2 + 10; y < height - h / 2 - 10; y += 22) {
        const reach = Math.hypot(x - home.x, y - home.y);
        if (reach < 320) patches.push({ x, y, reach });
      }
    patches.sort((a, b) => a.reach - b.reach);
    seats.push(...patches);
  }
  return seats;
}
// Twenty five tags on one wall is not a board, it is a wall of tags. What a
// label is for decides when it is drawn: a source relation is the evidence
// trail and may hang at any distance, a curatorial theme is an aside and only
// speaks when the reader is already holding one of its two papers. The rest is
// a budget: one tag per sentence, fourteen tags at most, and the ones the
// reader is looking at go up first.
/**
 * A label is an answer to a question, and at rest nobody has asked one. The
 * board used to hang fourteen of them over the year at all times, which is
 * how the whole-wall view ended up with paper labels lying across titles. A
 * relation says its name when the reader is holding one of its two papers,
 * by cursor, by keyboard or by the open record, and is silent otherwise.
 */
const LABEL_CAP = 14,
  CASE_CARD = { w: 104, h: 92 };
const labelQueue = computed(() => {
  const held = props.selected || hovered.value;
  if (!held) return [];
  const reach = (edge) => {
    const [a, b] = edge.ends || [];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 1e6;
  };
  return projectedEdges.value
    .filter((edge) => edge.from === held || edge.to === held)
    .map((edge) => ({
      edge,
      text: localText(edge.label, props.lang),
      near: true,
      reach: reach(edge),
    }))
    .sort((a, b) => a.edge.theme - b.edge.theme || a.reach - b.reach)
    .slice(0, LABEL_CAP);
});
const visibleEdges = computed(() => {
  // The month rail is already hanging there. Reserve its tabs first so a
  // relation label never lands on top of a month.
  const taken = visibleMonths.value.map((mark) => ({
      x: mark.x,
      y: mark.y,
      w: monthTabWidth(mark.text, mark.tight),
      h: 24,
    })),
    shown = [];
  // The case card is nailed to the bottom left corner of the frame, under the
  // grid rather than across the month rail, and does not travel with the
  // board: its berth is reserved before any tag is hung.
  taken.push({
    x: 8 + CASE_CARD.w / 2,
    y: (viewBox.value.height || 600) - 10 - CASE_CARD.h / 2,
    ...CASE_CARD,
  });
  for (const { edge, near } of labelQueue.value) {
    const [w, h] = tagBox(localText(edge.label, props.lang));
    const seats = tagSeats(edge, w, h);
    // A relation the reader is actually holding has to answer, even where the
    // cork around it is covered: the last resort for those is a berth that
    // only has to clear the other tags, and it lies on the paper the way a tag
    // pinned over a photograph does on a real board.
    const seat =
      seats.find((candidate) => clearOf(candidate, w, h, taken)) ||
      (near
        ? seats.find(
            (candidate) =>
              inFrame(candidate, w, h) && freeOf(candidate, w, h, taken),
          )
        : null);
    if (!seat) continue;
    taken.push({ x: seat.x, y: seat.y, w, h });
    const dx = edge.x - seat.x,
      dy = edge.y - seat.y,
      reach = Math.hypot(dx, dy);
    shown.push({
      ...edge,
      x: seat.x,
      y: seat.y,
      leader:
        seat.tied || reach < 14
          ? null
          : {
              left: seat.x + "px",
              top: seat.y + "px",
              width: reach + "px",
              transform: `rotate(${Math.atan2(dy, dx)}rad)`,
            },
    });
  }
  return shown;
});
// One note per paper is a note; three is wallpaper. The second note is a
// deterministic minority, and on a memo card it never sits on the title band.
const STICKY_LEGIBLE = 80;
function stickies(item) {
  const keywords = item.keywords || [];
  // A note nobody can read is a smudge on the neighbour it overhangs, so the
  // smallest sheets keep theirs until the reader leans in. The floor is the
  // width of a sheet at the whole-wall view: the board is wider than it was
  // and every paper sits a little smaller in the frame, and a threshold above
  // that width is a board that never shows a note at all. On the flat board
  // there is no projection and they are always drawn.
  const p = projection(item);
  if (p.width !== undefined && p.width < STICKY_LEGIBLE) return [];
  const slots =
    ["note", "clipping"].includes(labPaperShape(item))
      ? ["sticky-1", "sticky-2"]
      : ["sticky-0", "sticky-1"];
  return keywords
    .slice(0, labStickyCount(item))
    .map((keyword, i) => ({
      slot: slots[i],
      tone: i % 2,
      text: localText(keyword, props.lang),
    }));
}
// Lifting a sheet off the cork is the WebGL layer's job; remembering which
// sheet is in the reader's hand is this layer's, because that is what lets a
// curatorial theme speak its one line and then go quiet again. The cursor and
// the keyboard are two hands: a paper tabbed to stays in hand when the mouse
// wanders off some other corner of the board.
function lift(id, source = "pointed") {
  (source === "pointed" ? pointed : focused).value = id;
  engine?.hover(hovered.value);
}
const image = (item) => labImage(item);
const shapeOf = (item) => labPaperShape(item);
const groupOf = (item) => labGroup(item);
/**
 * How much a sheet says depends on how close the reader is (2026-09-24,
 * 哲宇：「資訊要顯示到什麼程度」). At the whole-wall view the paper itself says
 * what kind of evidence it is - a ticket, a score, a printout - with its date
 * and its title; leaned in, it adds who published or hosted it and its
 * keywords; close enough to read, the first sentence of the record. The rest
 * of the record is one click away. The thresholds are the sheet's own width on
 * screen, so they hold on a phone and a wide monitor alike.
 */
const ZOOM_MID = 180,
  ZOOM_NEAR = 270;
// The outlet that printed a cutting, as the catalog's first source names it,
// in place of a house masthead that said the same thing on every clipping.
const sourceName = (item) =>
  ((item.sources || [])[0]?.label || "")
    .split(" — ")[0]
    .replace(/ on YouTube$/, "")
    .trim();
const masthead = (item) => sourceName(item) || "CHE-YU WU · PRESS";
// The typed line along the top of a sheet with no picture: the kind of
// record, and where it happened when the source says.
function kindLine(item) {
  const kind = localText(LAB_KINDS[item.kind] || item.kind, props.lang),
    host = sourceName(item);
  return host && !/^che-yu wu$/i.test(host) ? `${kind} · ${host}` : kind;
}
// The record's own summary, printed on a sheet with no picture as far as the
// paper runs.
const lede = (item) => localText(item.summary, props.lang);
// A ticket stub is printed in one of four colours and carries a serial
// number, both off the record's own hash.
const ticketTone = (item) => Math.floor(labHash(item.id + "tone") * 4);
const stub = (item) =>
  "No. " + String(1000 + Math.floor(labHash(item.id + "stub") * 9000));
/**
 * Asking for a theme fades the rest of the board instead of flying the camera
 * to a lane, because there are no lanes any more. The reader keeps the place
 * on the year they had already found, and the same button pressed twice puts
 * the whole wall back.
 */
function pickTheme(id) {
  theme.value = theme.value === id ? null : id;
  engine?.highlight(theme.value);
}
// ---------------------------------------------------------------------------
// Flat reading: the year downwards, one month at a time
// ---------------------------------------------------------------------------
const flatMonths = computed(() => {
  const segments = new Map();
  for (const item of labOrdered(props.items)) {
    const key = item.date ? String(item.date).slice(0, 7) : "ongoing";
    if (!segments.has(key))
      segments.set(key, {
        key,
        text:
          key === "ongoing"
            ? t.value.ongoingMonth
            : key.length === 4
              ? `${key} · ${t.value.yearOnly}`
              : key.replace("-", " · "),
        items: [],
      });
    segments.get(key).items.push(item);
  }
  return [...segments.values()];
});
// Every relation of this record, written as the catalog wrote it. On the wall
// this is a string; here there is no room to draw one, so it is a tag that
// takes the reader to the other end of it.
const tiesOf = (item) => labRelations(props.items, item.id);
function jumpTo(id) {
  const card = document.getElementById("lab-flat-" + id);
  if (!card) return;
  card.scrollIntoView({
    behavior: reduced.value ? "auto" : "smooth",
    block: "center",
  });
  flashed.value = id;
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => (flashed.value = null), 1600);
}
// The phone's way onto the board. A wall three thousand units wide inside a
// column of text is not a wall, so the spatial view opens full screen, and
// leaving full screen puts the reader back in the timeline they came from.
function enterWall() {
  setMode("space", "button");
  toggleFullscreen();
}
const fastenOf = (item) => labFasten(item);
// Every nail on this sheet: the one holding it up and every one a string is
// tied to. The model hands out the same slots the WebGL strings were tied at.
const pinSlots = computed(() => labPinSlots(props.items));
const pinsOf = (item) => [...(pinSlots.value.get(item.id) || [])].sort();
const pinOf = (item) => labPinTone(item);
const projection = (item) =>
  positions.value.find((p) => p.id === item.id) || {};
// The longest unbreakable run in a title. Chinese breaks anywhere, so only a
// Latin word counts, and it is what decides how large the title may be set:
// "TouchDesigner" cut in half across two lines reads as two words that are
// not words, which is worse than a smaller line of type.
// The separators are written as escapes, not as literal full-width marks: a
// character class is not a sentence, and the prose gate reads this file.
const WORDS =
  /[^\s·—–/,:;()"'’\u3001\u30fb\u3002\uff0c\uff1a\uff1b\uff08\uff09]+/g;
const WIDE_CHAR = 0.68;
function longestWord(text) {
  let max = 0;
  for (const word of String(text).match(WORDS) || []) {
    if (/[\u2e80-\u9fff]/.test(word)) continue;
    if (word.length > max) max = word.length;
  }
  return max;
}
function labelSize(item, width) {
  const word = longestWord(localText(item.title, props.lang)),
    room = word ? (width * 0.92) / (word * WIDE_CHAR) : Infinity;
  return Math.max(
    9,
    Math.min(16, width / (props.lang === "en" ? 14.5 : 13), room),
  ).toFixed(2);
}
function paperStyle(item, index) {
  const p = projection(item);
  if (p.x !== undefined)
    return {
      left: p.x + "px",
      top: p.y + "px",
      width: p.width + "px",
      height: p.height + "px",
      transform: `translate(-50%,-50%) rotate(${p.rotation}rad)`,
      visibility: p.visible ? "visible" : "hidden",
      zIndex: 3 + (p.layer ?? index),
      "--lamp": p.lamp ?? 1,
      "--pin-top": (p.pinTop ?? 4) + "%",
      "--pin-side": (p.pinSide ?? 9) + "%",
      // English titles are longer than the Chinese ones and the two-line
      // clamp has to hold both, so the floor comes down to 9px and the
      // English divisor is a shade larger. The third term is the one that
      // keeps a long word whole. Everything else on the sheet is written as
      // a fraction of this, so the date shrinks with it.
      "--label-size": labelSize(item, p.width) + "px",
    };
  // Before the first projection the papers have no screen box yet. The old
  // fallback stacked every record at 20% width, which a background tab or a
  // hidden pane showed as one giant pile. Hold them until the frame lands.
  const n = labLayout(props.items).find((node) => node.id === item.id);
  return {
    left: 50 + n.x / 44 + "%",
    top: 50 - n.y / 18 + "%",
    width: "20%",
    height: n.height * 0.75 + "px",
    transform: `translate(-50%,-50%) rotate(${-n.rotation}rad)`,
    visibility: "hidden",
    "--lamp": n.lamp,
  };
}
function cleanup() {
  epoch++;
  engine?.dispose();
  engine = undefined;
  positions.value = [];
  projectedEdges.value = [];
  allEdges.value = [];
  projectedMonths.value = [];
  viewBox.value = { width: 0, height: 0 };
  wallScale.value = 1;
  wallZoom.value = 1;
  pointed.value = null;
  focused.value = null;
  drag = null;
  fingers = 0;
}
async function start() {
  cleanup();
  if (mode.value !== "space" || !props.items.length) return;
  const run = epoch;
  await nextTick();
  try {
    const { createLabWall } = await import("@/runtime/lab-wall.mjs");
    if (run !== epoch || !wall.value) return;
    engine = createLabWall(wall.value, props.items, {
      period: props.period,
      project: (value) => {
        positions.value = value.papers || [];
        wallScale.value = value.scale || 1;
        wallZoom.value = value.zoom || 1;
        projectedMonths.value = value.months || [];
        viewBox.value = value.view || { width: 0, height: 0 };
        allEdges.value = value.edges || [];
        projectedEdges.value = allEdges.value.filter((e) => e.visible);
      },
      onFailure: () => {
        fallback.value = true;
        setMode("flat", "webgl_failure");
      },
    });
    engine.select(props.selected);
    if (theme.value) engine.highlight(theme.value);
    // Dev only: the camera tween has to be observable from a test runner.
    if (import.meta.env.DEV) window.__labEngine = engine;
  } catch {
    if (run === epoch) {
      fallback.value = true;
      setMode("flat", "load_failure");
    }
  }
}
function keyboard(event) {
  if (event.target !== event.currentTarget) return;
  const actions = {
    ArrowLeft: () => engine?.pan(-130, 0),
    ArrowRight: () => engine?.pan(130, 0),
    ArrowUp: () => engine?.pan(0, 130),
    ArrowDown: () => engine?.pan(0, -130),
    "+": () => engine?.zoom(1.16),
    "=": () => engine?.zoom(1.16),
    "-": () => engine?.zoom(0.86),
    Home: () => engine?.reset(),
  };
  if (actions[event.key]) {
    event.preventDefault();
    actions[event.key]();
  }
}
// One finger drags the board, on a mouse and on a phone alike. A second
// finger means the reader is pinching, which the engine owns, so the drag
// stands down rather than fighting it for the same two pointers.
let fingers = 0;
function dragStart(event) {
  if (event.pointerType === "touch") fingers++;
  if (fingers > 1) {
    drag = null;
    return;
  }
  if (
    (event.pointerType !== "mouse" && event.pointerType !== "touch") ||
    (event.pointerType === "mouse" && event.button !== 0) ||
    event.target.closest("button")
  )
    return;
  drag = { x: event.clientX, y: event.clientY, id: event.pointerId };
  event.currentTarget.setPointerCapture(event.pointerId);
  if (event.pointerType === "mouse") event.preventDefault();
}
function dragMove(event) {
  if (!drag || event.pointerId !== drag.id || fingers > 1) return;
  // World units per dragged pixel, asked of the engine: the fit box is
  // measured off the layout now, so this can no longer be a constant.
  const factor = engine?.dragUnit || 4;
  engine?.drag(
    (drag.x - event.clientX) * factor,
    (event.clientY - drag.y) * factor,
  );
  drag.x = event.clientX;
  drag.y = event.clientY;
}
function dragEnd(event) {
  if (event?.pointerType === "touch") fingers = Math.max(0, fingers - 1);
  drag = null;
}
const motion = (event) => {
  reduced.value = event.matches;
  if (event.matches) setMode("flat", "reduced_motion");
};
onMounted(() => {
  media = matchMedia("(prefers-reduced-motion: reduce)");
  reduced.value = media.matches;
  media.addEventListener("change", motion);
  if (
    !media.matches &&
    innerWidth > 800 &&
    !/bot|crawler|spider|headless|prerender/i.test(navigator.userAgent)
  )
    setMode("space", "auto");
});
watch(mode, start);
watch(() => props.items, start);
watch(
  () => props.selected,
  (id) => engine?.select(id, true),
);
// Fullscreen takes the whole section (tools and board) so the controls stay
// reachable; browsers without the API get a fixed-position stand-in.
const fullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;
const syncFullscreen = () => {
  fullscreen.value = pseudoFull.value || fullscreenElement() === view.value;
  // The wall is only a wall at full screen on a phone. Coming out of it puts
  // the reader back in the month they were reading, not into a board three
  // thousand units wide squeezed into a column of text.
  if (!fullscreen.value && mode.value === "space" && innerWidth <= 800)
    setMode("flat", "fullscreen_exit");
};
async function toggleFullscreen() {
  const el = view.value;
  if (!el) return;
  if (pseudoFull.value) {
    pseudoFull.value = false;
    syncFullscreen();
    return;
  }
  if (fullscreenElement()) {
    await (document.exitFullscreen?.() ?? document.webkitExitFullscreen?.());
    return;
  }
  const request = el.requestFullscreen || el.webkitRequestFullscreen;
  try {
    if (!request) throw Error("no fullscreen api");
    await request.call(el);
  } catch {
    pseudoFull.value = true;
    syncFullscreen();
  }
}
const pseudoEscape = (event) => {
  if (event.key === "Escape" && pseudoFull.value) {
    pseudoFull.value = false;
    syncFullscreen();
  }
};
onMounted(() => {
  document.addEventListener("fullscreenchange", syncFullscreen);
  document.addEventListener("webkitfullscreenchange", syncFullscreen);
  document.addEventListener("keydown", pseudoEscape);
});
onBeforeUnmount(() => {
  cleanup();
  clearTimeout(flashTimer);
  media?.removeEventListener("change", motion);
  document.removeEventListener("fullscreenchange", syncFullscreen);
  document.removeEventListener("webkitfullscreenchange", syncFullscreen);
  document.removeEventListener("keydown", pseudoEscape);
  if (fullscreenElement() === view.value) document.exitFullscreen?.();
});
</script>
<style scoped>
.lab-clusters {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin: 0;
  font-size: 12px;
  color: #bbae8e;
}
.lab-clusters button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: none;
  border: 0;
  border-bottom: 1px solid #6c654f;
  color: #e4d7b1;
  min-height: 36px;
  padding: 5px 8px;
  cursor: pointer;
}
.lab-clusters button[aria-pressed="true"] {
  border-bottom-color: #cbb577;
  color: #eddaa5;
}
/* The same four pin heads that are on the board, so the button and the paper
   it picks out are recognisably the same colour. */
.lab-clusters button i {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.45);
}
.lab-view {
  margin: 10px 0 35px;
}
/* Fullscreen: the section becomes the room, the board fills what the tools leave. */
.lab-view:fullscreen,
.lab-view:-webkit-full-screen,
.lab-view.pseudo-full {
  position: fixed;
  inset: 0;
  z-index: 1000;
  margin: 0;
  padding: 12px 16px 14px;
  background: #0a0b0b;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.lab-view:fullscreen .lab-wall,
.lab-view:-webkit-full-screen .lab-wall,
.lab-view.pseudo-full .lab-wall {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
}
.lab-view:fullscreen .lab-flat-preview,
.lab-view:-webkit-full-screen .lab-flat-preview,
.lab-view.pseudo-full .lab-flat-preview {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.lab-view-tools button.lab-fullscreen[aria-pressed="true"] {
  border-color: #cbb577;
  color: #eddaa5;
}
.lab-view-tools {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.lab-view-tools > div {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}
.lab-view button {
  font: inherit;
  cursor: pointer;
}
.lab-view-tools button {
  min-height: 40px;
  padding: 7px 13px;
  background: #1a1310;
  color: #eae3d5;
  border: 0;
  border-radius: var(--r-pill);
  font-size: 12px;
  transition: var(--t-hover);
}
.lab-view-tools button[aria-pressed="true"] {
  background: #eddaa5;
  color: #1a1310;
}
.lab-view-tools button:disabled {
  opacity: 0.45;
  cursor: default;
}
/* The board hangs in a wooden frame: fourteen pixels of grain, then a dark
   seam where the cork is let into the rebate. */
.lab-wall {
  /* 2026-09-18 哲宇「擴大一點版面」: a taller frame, and the board is squarer
     to match (lab-model LAB_GRID_ASPECT), so the papers draw larger. */
  height: min(88vh, 920px);
  min-height: 600px;
  position: relative;
  overflow: hidden;
  border: 14px solid #6b4a2b;
  border-image: repeating-linear-gradient(
      104deg,
      #7a5430 0 5px,
      #5f3f22 5px 9px,
      #8a6138 9px 13px,
      #4e3319 13px 18px
    )
    14;
  background: #130d08;
  box-shadow:
    inset 0 0 0 2px #291a0c,
    inset 0 0 70px rgba(16, 8, 2, 0.75);
  /* Spatial mode owns the wheel and both fingers; flat mode is untouched. */
  touch-action: none;
  cursor: grab;
}
.lab-wall:active {
  cursor: grabbing;
}
.lab-wall :deep(canvas) {
  z-index: 0;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.lab-paper-layer,
.lab-edge-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.lab-edge-layer {
  z-index: 60;
}
/* Red string is evidence, twine is a curatorial adjacency. Both run behind
   the paper and show only on the cork between the sheets: a net drawn over
   fifty sheets of small type is a net over the writing. The strings of the
   sheet in the reader's hand are drawn again in a second layer, over
   everything, and those are the only ones that cross the paper. */
.lab-string-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
}
.lab-string-layer.front {
  z-index: 56;
}
.lab-string-layer polyline {
  fill: none;
  stroke: #bd2f26;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.7;
}
.lab-string-layer polyline.theme {
  stroke: #e6d6ae;
  opacity: 0.55;
}
.lab-string-layer polyline.dim {
  opacity: 0.45;
}
.lab-string-layer.front polyline,
.lab-string-layer.front polyline.theme {
  opacity: 1;
}
/* A tag of paper hanging on the string, carrying the catalog's own label. */
.lab-edge-tag {
  position: absolute;
  pointer-events: none;
  transform: translate(-50%, -50%) rotate(-1.4deg);
  background: #f3ecd9;
  color: #3a2e1e;
  font-family: "Caveat", var(--font-display), cursive;
  font-size: var(--tag-size, 13px);
  line-height: 1.05;
  padding: 4px 7px 5px;
  white-space: nowrap;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 2px 3px 5px rgba(12, 6, 2, 0.5);
  border-left: 3px solid #c3322b;
}
.lab-edge-tag.theme {
  border-left-color: #cbb98f;
  background: #ece3cd;
}
/* When a tag cannot hang on its own string it stands beside the nail, and
   this hairline says which string it belongs to. */
.lab-edge-leader {
  position: absolute;
  height: 1px;
  transform-origin: 0 50%;
  background: #a8322a;
  opacity: 0.75;
  pointer-events: none;
}
.lab-edge-leader.theme {
  background: #c9b78c;
  opacity: 0.6;
}
.lab-paper {
  position: absolute;
  display: block;
  padding: 0;
  border: 0;
  background: transparent;
  pointer-events: auto;
  text-align: left;
  color: #22251c;
  filter: brightness(var(--lamp, 1));
  transition: filter 0.15s;
  overflow: visible;
  z-index: 3;
}
/* The printed sheet, and the only thing on a paper that clips its contents.
   A note stuck on the corner used to be cut off along the paper's edge -
   worst of all on a torn notebook page, where the clip-path that makes the
   tear took the note and the tape with it. The clipping lives one layer in
   now: the button is the pin board's idea of the record and overflows, this
   is the paper itself. */
.lab-sheet {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: var(--sheet);
}
/* Five kinds of paper. The WebGL sheet under each one carries the same
   colour, so an edge or a tape strip never shows a different white. */
.lab-paper.shape-polaroid {
  --sheet: #fbf7ee;
}
.lab-paper.shape-polaroid .lab-sheet {
  border: 1px solid rgba(120, 101, 72, 0.25);
}
.lab-paper.shape-capture {
  --sheet: #fcfbf7;
}
.lab-paper.shape-capture .lab-sheet {
  border: 1px solid rgba(120, 101, 72, 0.3);
}
.lab-paper.shape-clipping {
  --sheet: #eee2c4;
}
.lab-paper.shape-clipping .lab-sheet {
  border-top: 5px solid #23201a;
}
/* A page out of a notebook: a red margin and a torn top edge. The blue rules
   went on 2026-09-24 (哲宇：「不要放太多無意義的線條」): the page is ruled by
   the record's own words now, not by lines standing in for them. */
.lab-paper.shape-note {
  --sheet: #e3ebef;
}
.lab-paper.shape-note .lab-sheet {
  border-left: 3px solid #bf5a4b;
  clip-path: polygon(
    0 5%,
    7% 1%,
    15% 5%,
    23% 0,
    32% 4%,
    41% 0,
    50% 5%,
    59% 1%,
    68% 5%,
    77% 0,
    86% 4%,
    94% 1%,
    100% 5%,
    100% 100%,
    0 100%
  );
}
/* A kraft evidence tag with an eyelet and a short length of twine. */
.lab-paper.shape-tag {
  --sheet: #cbaf82;
}
.lab-paper.shape-tag .lab-sheet {
  background-image: linear-gradient(
    118deg,
    rgba(255, 255, 255, 0.14),
    rgba(90, 64, 30, 0.12)
  );
  border: 1px solid rgba(96, 70, 36, 0.5);
}
.lab-tag-eyelet {
  position: absolute;
  top: 7%;
  left: 50%;
  width: 11%;
  aspect-ratio: 1;
  margin-left: -5.5%;
  border-radius: 50%;
  background: #1c1205;
  box-shadow: 0 0 0 2px rgba(255, 250, 236, 0.55);
}
.lab-tag-eyelet::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 90%;
  width: 2px;
  height: 260%;
  background: repeating-linear-gradient(
    -28deg,
    #d9c79e 0 3px,
    #a98f5e 3px 6px
  );
  transform-origin: 50% 100%;
  transform: rotate(16deg);
}
/* A cutting with a photograph in it (lab-model LAB_CLIPPING_BOX): the
   masthead, the picture across the column, then the headline and the
   article. The contract test reads the two picture numbers back. */
.lab-paper.shape-clipping .lab-sheet > img {
  top: 12.5%;
  height: 46.1%;
  left: 6%;
  width: 88%;
}
.lab-paper.shape-clipping.has-image .lab-paper-copy {
  inset: 60.6% 0 4%;
  padding: 0 6%;
  background: transparent;
}
/* An exhibition print: a photograph of the show at a size up from the rest,
   with its wall label under it - date, title and the first lines of what the
   show was. Its size says what it is; the red stamp it carried for an hour on
   2026-09-24 said it again, louder (哲宇：「展覽那個tag好醜」). */
.lab-paper.shape-exhibit {
  --sheet: #fbf7ee;
}
.lab-paper.shape-exhibit .lab-sheet {
  border: 1px solid rgba(120, 101, 72, 0.25);
}
/* The print's own picture box and label band (lab-model LAB_EXHIBIT_BOX; the
   contract test reads these two numbers back like the photograph's). */
.lab-paper.shape-exhibit .lab-sheet > img {
  top: 3.94%;
  height: 47.24%;
}
.lab-paper.shape-exhibit.has-image .lab-paper-copy {
  inset: 51.18% 0 0;
  padding: 3% 6% 4%;
}
.lab-paper.shape-exhibit .lab-paper-lede {
  margin-top: 4px;
  font-size: calc(var(--label-size, 16px) * 0.6);
  line-height: 1.5;
}
/* An index card, five by three: a typed heading and one red rule under it.
   A talk, a course, a workshop. */
.lab-paper.shape-card {
  --sheet: #fbf8ef;
}
.lab-paper.shape-card .lab-sheet {
  border: 1px solid rgba(120, 101, 72, 0.22);
  background-image: linear-gradient(
    180deg,
    transparent 24%,
    rgba(196, 64, 52, 0.72) 24%,
    rgba(196, 64, 52, 0.72) calc(24% + 1.5px),
    transparent calc(24% + 1.5px)
  );
}
.lab-paper.shape-card .lab-paper-copy {
  inset: 29% 0 6%;
  padding: 0 7%;
}
/* Line-printer paper: feed holes down both margins behind a perforation,
   and the record printed in the middle in a typewriter face. What a
   repository looks like before anyone screenshots it. */
.lab-paper.shape-printout {
  --sheet: #f3f1e7;
}
.lab-paper.shape-printout .lab-sheet::before,
.lab-paper.shape-printout .lab-sheet::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  width: 9%;
  background:
    radial-gradient(circle, #5b3f25 0 30%, transparent 34%) 50% 3% / 100% 10%
      repeat-y,
    var(--sheet);
}
.lab-paper.shape-printout .lab-sheet::before {
  left: 0;
  border-right: 1px dashed rgba(90, 80, 60, 0.45);
}
.lab-paper.shape-printout .lab-sheet::after {
  right: 0;
  border-left: 1px dashed rgba(90, 80, 60, 0.45);
}
.lab-paper.shape-printout .lab-paper-copy {
  inset: 22% 12% 8%;
  padding: 0;
}
.lab-paper.shape-printout strong,
.lab-paper.shape-printout small {
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  letter-spacing: -0.02em;
}
.lab-paper.shape-printout strong {
  font-size: calc(var(--label-size, 16px) * 0.92);
}
.lab-paper.shape-printout small {
  font-size: calc(var(--label-size, 16px) * 0.64);
  color: #4d6a4d;
}
/* A sheet of music paper: a pair of notes in the corner and the piece's own
   description where the staves would be. Staves with nothing on them were
   the loudest meaningless lines on the board (2026-09-24). */
.lab-paper.shape-score {
  --sheet: #f7f1e1;
}
.lab-paper.shape-score .lab-sheet {
  border: 1px solid rgba(120, 101, 72, 0.2);
  border-top: 3px double rgba(70, 58, 44, 0.45);
}
.lab-paper.shape-score .lab-sheet::before {
  content: "\266B";
  position: absolute;
  right: 8%;
  top: 7%;
  font-size: calc(var(--label-size, 16px) * 1.35);
  line-height: 1;
  color: rgba(150, 52, 40, 0.6);
}
.lab-paper.shape-score .lab-paper-copy {
  inset: 18% 0 6%;
  padding: 0 8%;
}
/* A ticket stub: a perforation down the right with a notch at each end, the
   serial number on the stub and the event on the rest. Four colours of card,
   off the record's hash, the way a drawer of old tickets is never one colour. */
.lab-paper.shape-ticket {
  --sheet: #e8d6a6;
}
.lab-paper.shape-ticket.tone-1 {
  --sheet: #d8e2cf;
}
.lab-paper.shape-ticket.tone-2 {
  --sheet: #ecd3c7;
}
.lab-paper.shape-ticket.tone-3 {
  --sheet: #d6dee7;
}
.lab-paper.shape-ticket .lab-sheet {
  background-image: linear-gradient(
    115deg,
    rgba(255, 255, 255, 0.2),
    rgba(120, 80, 30, 0.1)
  );
  -webkit-mask-image:
    radial-gradient(ellipse 5.5% 11% at 76% 0, transparent 97%, #000 100%),
    radial-gradient(ellipse 5.5% 11% at 76% 100%, transparent 97%, #000 100%);
  -webkit-mask-composite: source-in;
  mask-image:
    radial-gradient(ellipse 5.5% 11% at 76% 0, transparent 97%, #000 100%),
    radial-gradient(ellipse 5.5% 11% at 76% 100%, transparent 97%, #000 100%);
  mask-composite: intersect;
}
.lab-paper.shape-ticket .lab-sheet::after {
  content: "";
  position: absolute;
  left: 76%;
  top: 13%;
  bottom: 13%;
  border-left: 2px dotted rgba(90, 60, 25, 0.45);
}
.lab-stub {
  position: absolute;
  left: 79%;
  right: 3%;
  top: 14%;
  bottom: 14%;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: calc(var(--label-size, 16px) * 0.62);
  font-style: normal;
  letter-spacing: 0.16em;
  color: rgba(90, 60, 25, 0.75);
  white-space: nowrap;
}
.lab-paper.shape-ticket .lab-paper-copy {
  inset: 16% 26% 8% 0;
  padding: 0 6%;
}
/* The typed heading of a sheet with no picture: what kind of record it is
   and where it happened. */
.lab-kind-line {
  position: absolute;
  top: 7%;
  left: 7%;
  right: 7%;
  display: block;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: calc(var(--label-size, 16px) * 0.54);
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #7a5e3c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lab-paper.shape-note .lab-kind-line {
  top: 9%;
}
.lab-paper.shape-printout .lab-kind-line {
  top: 8%;
  left: 12%;
  right: 12%;
  color: #4d6a4d;
}
.lab-paper.shape-ticket .lab-kind-line {
  right: 27%;
}
.lab-paper.shape-score .lab-kind-line {
  right: 20%;
}
/* On a kraft tag the eyelet takes the top, so the heading goes at the foot. */
.lab-paper.shape-tag .lab-kind-line {
  top: auto;
  bottom: 8%;
  left: 9%;
  right: 9%;
  color: #5b4020;
}
/* Press cutting: a masthead rule and two columns of grey newsprint. */
.lab-masthead {
  position: absolute;
  top: 4%;
  left: 6%;
  right: 6%;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: calc(var(--label-size, 14px) * 0.56);
  letter-spacing: 0.22em;
  color: #23201a;
  border-bottom: 2px solid #23201a;
  padding-bottom: 3px;
  /* One line of masthead, always. Wrapped, it used to push down into the
     headline and print the date on top of itself. */
  white-space: nowrap;
  overflow: hidden;
}
.lab-credit {
  position: absolute;
  right: 4%;
  bottom: 2%;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: calc(var(--label-size, 14px) * 0.48);
  font-style: normal;
  color: #7d7364;
  pointer-events: none;
}
/* Tape: two strips of translucent masking tape across the top corners. */
.lab-tape {
  position: absolute;
  top: -5%;
  width: 30%;
  height: 13%;
  background: linear-gradient(
    180deg,
    rgba(240, 224, 176, 0.72),
    rgba(214, 193, 143, 0.62)
  );
  box-shadow: 0 1px 3px rgba(20, 11, 3, 0.35);
  pointer-events: none;
}
.lab-tape.left {
  left: -7%;
  transform: rotate(-38deg);
}
.lab-tape.right {
  right: -7%;
  transform: rotate(38deg);
}
/* Asked for one theme, the rest of the board steps back: still legible as
   paper, no longer competing for the eye. */
.lab-paper.muted {
  opacity: 0.35;
  filter: brightness(var(--lamp, 1)) grayscale(0.85);
}
.lab-paper.selected {
  outline: 3px solid #f0c765;
  outline-offset: 2px;
  box-shadow: 0 0 22px rgba(240, 199, 101, 0.5);
}
.lab-paper.neighbor {
  outline: 1px dashed #cbb577;
  outline-offset: 5px;
}
.lab-paper-copy {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2px;
  padding: 0 4%;
  position: absolute;
  inset: 11% 0 5%;
  overflow: hidden;
}
.lab-paper.shape-note .lab-paper-copy {
  /* Clear of the torn edge along the top. */
  inset: 15% 0 6%;
}
.lab-paper.shape-clipping .lab-paper-copy {
  inset: 25% 0 5%;
}
.lab-paper.shape-tag .lab-paper-copy {
  inset: 24% 0 7%;
  padding: 0 8%;
}
/* The caption band under a photograph is painted here, not in WebGL, so the
   two-line title always has paper behind it. --lamp is the paper's own
   distance to the lamp: the bottom right of the board really is darker. */
/* The paper is now cut to its own contents: the model sizes it as border plus
   a 3:2 picture plus a caption band of 0.32 of the width, so the band starts
   exactly where the picture stops and runs to the bottom edge. No sheet can
   end in the blank half that Taiwan.md used to show. */
.lab-paper.has-image .lab-paper-copy {
  inset: 60.67% 0 0;
  height: auto;
  max-height: none;
  padding: 3% 5% 4%;
  justify-content: flex-start;
  background: var(--sheet, #faf5e9);
}
.lab-paper strong {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 0 0 auto;
  font-family: var(--font-display);
  font-size: var(--label-size, 16px);
  font-weight: 500;
  line-height: 1.28;
  letter-spacing: -0.012em;
  /* A word is broken only when the word alone will not fit the line, never
     mid-word for convenience: "TouchDesigner" is one word on this board, and
     labelSize() has already made the type small enough to hold it. */
  overflow-wrap: break-word;
  word-break: normal;
  hyphens: none;
}
.lab-paper small {
  flex: 0 0 auto;
  font-family: "Caveat", var(--font-display), cursive;
  font-size: calc(var(--label-size, 16px) * 0.82);
  line-height: 1.1;
  white-space: nowrap;
  letter-spacing: 0.02em;
  color: #6a4f32;
}
.lab-paper-keywords {
  display: none;
}
.lab-paper.zoom-mid .lab-paper-keywords {
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: calc(var(--label-size, 16px) * 0.66);
  line-height: 1.45;
  opacity: 0.75;
  min-height: 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
/* The record's own summary, set small and run to the foot of the sheet, where
   a real document would carry on past the fold. From across the room it is
   the grey of type; leaned in, it reads. It replaced the ruled lines, staves
   and dummy newsprint that used to fill the same space with nothing. */
.lab-paper-lede {
  display: block;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  margin-top: 4px;
  font-size: calc(var(--label-size, 16px) * 0.6);
  line-height: 1.5;
  color: #4a3f30;
  -webkit-mask-image: linear-gradient(180deg, #000 72%, transparent);
  mask-image: linear-gradient(180deg, #000 72%, transparent);
}
/* A cutting sets it in two newspaper columns under the headline. */
.lab-paper.shape-clipping .lab-paper-lede {
  column-count: 2;
  column-gap: 7%;
  column-fill: auto;
  text-align: justify;
  font-family: var(--font-display);
}
.lab-paper.shape-printout .lab-paper-lede {
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  letter-spacing: -0.02em;
  color: #3f5a3f;
}
.lab-paper.zoom-near .lab-paper-lede {
  font-size: calc(var(--label-size, 16px) * 0.66);
}
/* A photograph inside a 5% border, printed 3:2. Both numbers are the model's
   own LAB_IMAGE_BOX, which is why the caption band below can start exactly
   where the picture stops: 14 units of border and 168 of picture on a sheet
   280 by 300. The contract test reads these two percentages out of this file
   and checks them against the model, so the two can never drift apart. */
.lab-sheet > img {
  position: absolute;
  inset: 4.67% 5% auto;
  height: 56%;
  width: 90%;
  object-fit: cover;
  display: block;
  background: #17140f;
}
/* Every screenshot in the catalog is shot 1200x800, which is the 3:2 the
   sheet is cut to, so the frame is filled rather than boxed. */
.lab-paper.shape-capture .lab-sheet > img {
  background: #1b1a17;
}
/* Sticky notes: his own keywords, pressed onto the corner of the paper. */
.lab-sticky {
  position: absolute;
  font-style: normal;
  font-family: "Caveat", var(--font-display), cursive;
  font-size: calc(var(--label-size, 16px) * 0.85);
  line-height: 1.1;
  color: #3b3020;
  background: #f5df7a;
  padding: 4px 6px;
  max-width: 62%;
  box-shadow: 1px 2px 4px rgba(14, 7, 2, 0.45);
  pointer-events: none;
  /* One line, clipped with an ellipsis. A note broken to one letter a line
     is not a note, and the overhang lands on whatever is pinned next door. */
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.lab-sticky.tone-1 {
  background: #f3c7c0;
}
/* A note pressed onto a corner overhangs it by about a quarter of itself.
   Four percent was all the old board dared, because the rest was being cut
   off anyway; now that the paper clips one layer in, the note can sit the way
   a hand presses one on. */
.lab-sticky.sticky-0 {
  top: -9%;
  right: -12%;
  transform: rotate(4.5deg);
}
.lab-sticky.sticky-1 {
  bottom: -8%;
  left: -12%;
  transform: rotate(-5deg);
}
.lab-sticky.sticky-2 {
  bottom: -9%;
  right: -11%;
  transform: rotate(3deg);
}
.lab-paper.shape-note .lab-sticky.sticky-1,
.lab-paper.shape-clipping .lab-sticky.sticky-1 {
  bottom: -10%;
  left: -14%;
}
/* The pins. Drawn here, not in WebGL, because a pin behind the paper it holds
   up is not a pin. Slot 0 is the top centre; 1 and 2 are the top corners the
   extra strings are tied to. */
/* Four pin heads, one per curated theme. The theme used to be the height a
   record hung at; it is this colour now, and the four buttons above the wall
   carry the same four dots. */
.lab-pin {
  position: absolute;
  top: var(--pin-top, 4%);
  width: calc(var(--label-size, 14px) * 0.72);
  aspect-ratio: 1;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle at 33% 28%, #f3a8a1, #a5241d 68%);
  box-shadow: 1px 2px 3px rgba(8, 4, 1, 0.6);
  pointer-events: none;
}
.lab-pin.pin-art {
  background: radial-gradient(circle at 33% 28%, #7b7671, #16150f 68%);
}
.lab-pin.pin-exchange {
  background: radial-gradient(circle at 33% 28%, #ffffff, #bfb7a2 68%);
}
.lab-pin.pin-echo {
  background: radial-gradient(circle at 33% 28%, #9cc3ea, #27547f 68%);
}
.lab-pin.slot-0 {
  left: 50%;
}
.lab-pin.slot-1 {
  left: var(--pin-side, 9%);
}
.lab-pin.slot-2 {
  left: calc(100% - var(--pin-side, 9%));
}
/* The month rail along the top of the board: small paper tabs, one pin each,
   each one a jump to that month. */
.lab-month-rail {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}
.lab-month {
  position: absolute;
  transform: translate(-50%, -50%) rotate(-1deg);
  pointer-events: auto;
  background: #f6f0e0;
  color: #3a2e1e;
  border: 0;
  padding: 6px 9px 5px;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 2px 3px 5px rgba(12, 6, 2, 0.5);
  cursor: pointer;
}
/* The last step back before a month would lose its tab: same paper, smaller
   print, the padding cut to the bone. Still a tab, still one click. */
.lab-month.tight {
  padding: 4px 3px 3px;
  font-size: 10px;
  letter-spacing: 0.02em;
}
.lab-month i {
  position: absolute;
  top: -4px;
  left: 50%;
  width: 7px;
  height: 7px;
  margin-left: -3.5px;
  border-radius: 50%;
  background: radial-gradient(circle at 33% 30%, #fdf2d8, #9b8a63 70%);
}
.lab-wall-hint {
  position: absolute;
  bottom: 12px;
  right: 16px;
  pointer-events: none;
  font-size: 11px;
  color: #e3d5b6;
  background: #130d08e0;
  padding: 5px 8px;
  z-index: 4;
}
/* The case label, nailed to the bottom left corner of the frame. It used to
   be at the top, where it sat across the first two month tabs of the rail;
   the empty cork under the first column is the place for it. */
.lab-case-card {
  position: absolute;
  bottom: 10px;
  left: 8px;
  z-index: 70;
  display: grid;
  gap: 2px;
  padding: 11px 12px 10px;
  background: #f2ead6;
  color: #3a2c1c;
  transform: rotate(-1.4deg);
  box-shadow: 4px 6px 12px rgba(10, 5, 1, 0.55);
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 10px;
  line-height: 1.5;
  letter-spacing: 0.02em;
  pointer-events: none;
}
.lab-case-card b {
  font-family: var(--font-display);
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 500;
}
.lab-case-pin {
  position: absolute;
  top: -6px;
  left: 50%;
  width: 11px;
  height: 11px;
  margin-left: -5px;
  border-radius: 50%;
  background: radial-gradient(circle at 33% 30%, #f0a09a, #a5241d 68%);
  box-shadow: 1px 2px 3px rgba(8, 4, 1, 0.6);
}
/* Flat reading: the same cork, but the year read downwards. One section per
   month, a length of twine down the left of it with the month tab pinned to
   the top, and that month's records in two columns beside the twine. No
   strings are drawn: a relation is a tag under the card that carries it. */
.lab-flat-preview {
  position: relative;
  padding: 22px 18px 30px;
  background-color: #9c6e45;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='cork' x='0' y='0' width='100%25' height='100%25'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='240' height='240' filter='url(%23cork)' opacity='0.85'/%3E%3C/svg%3E"),
    radial-gradient(
      circle at 18% 12%,
      rgba(255, 226, 170, 0.42),
      rgba(255, 226, 170, 0) 58%
    ),
    radial-gradient(
      circle at 78% 82%,
      rgba(58, 33, 14, 0.5),
      rgba(58, 33, 14, 0) 62%
    ),
    linear-gradient(
      72deg,
      rgba(120, 82, 48, 0.55),
      rgba(168, 120, 74, 0.2) 48%,
      rgba(96, 64, 36, 0.5)
    );
  background-size:
    240px 240px,
    100% 100%,
    100% 100%,
    100% 100%;
  background-blend-mode: soft-light, normal, normal, normal;
  box-shadow: inset 0 0 90px rgba(30, 15, 4, 0.75);
}
.lab-flat-top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 26px;
}
.lab-flat-hint {
  flex: 1 1 260px;
  margin: 0;
  padding: 9px 13px;
  background: rgba(19, 12, 6, 0.72);
  color: #ecdcb8;
  font-size: 12px;
  line-height: 1.8;
}
.lab-flat-enter {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 10px 16px;
  background: #1a1310;
  color: #eddaa5;
  border: 0;
  border-radius: var(--r-pill);
  font-size: 13px;
  transition: var(--t-hover);
}
.lab-case-card.flat {
  position: static;
  flex: 0 0 auto;
  margin: 6px 4px 0 0;
}
/* A month: the twine, the tab, the papers. */
.lab-flat-month {
  position: relative;
  padding: 0 0 26px 34px;
}
.lab-flat-month::before {
  content: "";
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 11px;
  width: 3px;
  background: repeating-linear-gradient(
    -70deg,
    #d9c79e 0 4px,
    #a2875a 4px 8px
  );
  opacity: 0.85;
}
.lab-flat-month-tab {
  position: relative;
  display: inline-block;
  margin: 0 0 16px -22px;
  padding: 6px 12px 5px;
  background: #f6f0e0;
  color: #3a2e1e;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.12em;
  transform: rotate(-1.1deg);
  box-shadow: 2px 3px 6px rgba(12, 6, 2, 0.55);
}
.lab-flat-month-tab::after {
  content: "";
  position: absolute;
  top: -5px;
  left: 50%;
  width: 9px;
  height: 9px;
  margin-left: -4.5px;
  border-radius: 50%;
  background: radial-gradient(circle at 33% 30%, #fdf2d8, #9b8a63 70%);
}
.lab-flat-cards {
  columns: 2;
  column-gap: 18px;
}
@media (min-width: 1100px) {
  .lab-flat-cards {
    columns: 3;
    column-gap: 22px;
  }
}
.lab-flat-card {
  position: relative;
  overflow: visible;
  display: inline-block;
  width: 100%;
  margin: 0 0 26px;
  padding: 12px 12px 10px;
  background: #f7f1e2;
  color: #252720;
  outline: 2px solid transparent;
  break-inside: avoid;
  transform: rotate(-0.7deg);
  box-shadow: 7px 9px 16px rgba(14, 7, 2, 0.5);
}
.lab-flat-card:nth-child(2n) {
  transform: rotate(0.8deg);
}
.lab-flat-card:nth-child(3n) {
  transform: rotate(-1.4deg);
}
.lab-flat-card.shape-note {
  background: #e3ebef;
  border-left: 4px solid #bf5a4b;
}
.lab-flat-card.shape-clipping {
  background: #eee2c4;
  border-top: 7px solid #23201a;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
}
.lab-flat-card.shape-tag {
  background: #cbaf82;
}
.lab-flat-card.shape-capture {
  background: #fcfbf7;
}
.lab-flat-card.shape-exhibit {
  background: #fbf7ee;
  box-shadow:
    inset 0 0 0 1px rgba(178, 38, 30, 0.35),
    7px 9px 16px rgba(14, 7, 2, 0.5);
}
/* The four papers a record with no picture can be filed on (lab-model
   labPaperShape), in the flat reading: the same colours, the same one cue
   each - a red rule, feed holes, a double rule, a perforation. */
.lab-flat-card.shape-card {
  background: #fbf8ef;
  box-shadow:
    inset 0 3px 0 #fbf8ef,
    inset 0 4.5px 0 rgba(196, 64, 52, 0.7),
    7px 9px 16px rgba(14, 7, 2, 0.5);
}
.lab-flat-card.shape-printout {
  background: #f3f1e7;
  border-left: 3px dotted rgba(91, 63, 37, 0.55);
  border-right: 3px dotted rgba(91, 63, 37, 0.55);
  font-family: "IBM Plex Mono", ui-monospace, monospace;
}
.lab-flat-card.shape-score {
  background: #f7f1e1;
  border-top: 3px double rgba(70, 58, 44, 0.45);
}
.lab-flat-card.shape-ticket {
  background: #e8d6a6;
  border-right: 2px dotted rgba(90, 60, 25, 0.5);
}
.lab-flat-card.selected {
  outline-color: #b3984f;
}
/* The card the reader was just sent to, so a jump down the page lands
   somewhere visible rather than somewhere plausible. */
.lab-flat-card.flash {
  outline-color: #f0c765;
  box-shadow:
    7px 9px 16px rgba(14, 7, 2, 0.5),
    0 0 0 4px rgba(240, 199, 101, 0.45);
}
.lab-flat-open {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
}
.lab-flat-open img {
  width: 100%;
  aspect-ratio: 3 / 2;
  object-fit: cover;
  display: block;
  background: #17140f;
}
.lab-flat-open > span {
  padding: 12px 4px 6px;
  display: grid;
  gap: 8px;
}
.lab-flat-card strong {
  font-family: var(--font-display);
  font-size: 17px;
  line-height: 1.5;
  font-weight: 500;
  /* A title on a phone is the whole of the record the reader can see, so it
     is never cut: it wraps and the card grows. */
  overflow-wrap: break-word;
}
.lab-flat-card small {
  font-family: "Caveat", var(--font-display), cursive;
  font-size: 16px;
  color: #6a4f32;
}
.lab-flat-card span > span {
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 11px;
  line-height: 1.7;
  color: #4e4536;
}
.lab-flat-card.shape-clipping strong {
  font-family: var(--font-display);
}
/* What the wall would draw as a string. The label is the catalog's own, and
   pressing it takes the reader to the record at the other end. */
.lab-flat-ties {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0 0;
  padding: 9px 2px 0;
  list-style: none;
  border-top: 1px dashed rgba(74, 58, 34, 0.4);
}
.lab-flat-ties button {
  --tie-c: #c3322b;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 0;
  border-radius: var(--r-sm);
  background: #efe6d0;
  color: #3a2e1e;
  font-family: "Caveat", var(--font-display), cursive;
  font-size: 15px;
  line-height: 1.2;
  padding: 7px 10px;
  min-height: 34px;
  text-align: left;
}
.lab-flat-ties button::before {
  content: "";
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--tie-c);
}
.lab-flat-ties button.theme {
  --tie-c: #b9a273;
  background: #e7dcc2;
}
.lab-flat-preview .lab-sticky {
  font-size: 13px;
  max-width: 46%;
}
.lab-flat-preview .lab-sticky.sticky-0 {
  top: -9px;
  right: -10px;
}
.lab-flat-preview .lab-sticky.sticky-1 {
  bottom: -10px;
  left: -10px;
}
.lab-flat-preview .lab-sticky.sticky-2 {
  bottom: -10px;
  right: -10px;
}
.lab-fallback {
  font-size: 13px;
  color: #c7b991;
}
.lab-view button:focus-visible,
.lab-wall:focus-visible {
  outline: 2px solid #dfc27a;
  outline-offset: 4px;
}
/* Full screen on a phone: the board is the point, and eight buttons of
   chrome were taking a third of the screen before it. The two things a
   reader still needs are the way out and the way back to the whole wall. */
@media (max-width: 800px) {
  .lab-view:fullscreen .lab-clusters,
  .lab-view:-webkit-full-screen .lab-clusters,
  .lab-view.pseudo-full .lab-clusters,
  .lab-view:fullscreen .lab-navigation button:nth-child(-n + 6),
  .lab-view:-webkit-full-screen .lab-navigation button:nth-child(-n + 6),
  .lab-view.pseudo-full .lab-navigation button:nth-child(-n + 6) {
    display: none;
  }
  .lab-view:fullscreen .lab-view-tools,
  .lab-view:-webkit-full-screen .lab-view-tools,
  .lab-view.pseudo-full .lab-view-tools {
    margin-bottom: 8px;
  }
}
@media (max-width: 800px) {
  .lab-flat-preview {
    padding: 18px 12px 24px;
  }
  .lab-flat-month {
    padding-left: 26px;
  }
  .lab-flat-month::before {
    left: 8px;
  }
  .lab-flat-cards {
    column-gap: 14px;
  }
  .lab-flat-card {
    padding: 10px 10px 8px;
    margin-bottom: 20px;
  }
  .lab-flat-card strong {
    font-size: 15px;
    line-height: 1.55;
  }
  .lab-flat-open > span {
    padding: 10px 2px 4px;
    gap: 6px;
  }
  .lab-flat-card small {
    font-size: 14px;
  }
  .lab-flat-ties button {
    font-size: 14px;
    padding: 6px 7px;
  }
  /* A note stuck on the corner of a card 150 pixels wide covers the title of
     the card beside it, and there is no room on a phone for a second voice. */
  .lab-flat-preview .lab-sticky {
    display: none;
  }
  .lab-wall {
    min-height: 470px;
  }
  .lab-view-tools {
    gap: 10px;
  }
}
@media (max-width: 600px) {
  .lab-flat-top {
    gap: 10px;
  }
  .lab-case-card.flat {
    margin: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
  }
  .lab-flat-card {
    transform: none !important;
  }
}
</style>
